import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      if (!token) {
        setLoading(false);
        return;
      }
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch {
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('session_token', res.data.session_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem('session_token', res.data.session_token);
    setUser(res.data.user);

    // Track referral if user signed up via referral link
    const referralCode = localStorage.getItem('referral_code');
    if (referralCode && res.data.user?.user_id) {
      try {
        await axios.post(`${API}/referral/track-signup`, {
          referral_code: referralCode,
          user_id: res.data.user.user_id
        });
        localStorage.removeItem('referral_code');
      } catch { /* ignore */ }
    }

    return res.data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google-session`, { session_id: sessionId });
    localStorage.setItem('session_token', res.data.session_token);
    setUser(res.data.user);

    // Track referral if user signed up via referral link
    const referralCode = localStorage.getItem('referral_code');
    if (referralCode && res.data.user?.user_id) {
      try {
        await axios.post(`${API}/referral/track-signup`, {
          referral_code: referralCode,
          user_id: res.data.user.user_id
        });
        localStorage.removeItem('referral_code');
      } catch { /* ignore */ }
    }

    return res.data.user;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { /* ignore */ }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  const updateProfile = async (data) => {
    const token = localStorage.getItem('session_token');
    const res = await axios.put(`${API}/auth/profile`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, loginWithGoogle, handleGoogleCallback, logout, updateProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
